import { useState } from 'react';
import { studioApi, useActSources, useCreateAct, useSendAct, useUpdateAct } from '@/api/studio';
import type { Act, ActSourceLine, Project } from '@/api/types';
import { DownloadButton } from '@/components/DownloadButton';
import { LinesEditor } from '@/components/LinesEditor';
import { Dialog } from '@/components/ui';
import { useStudioTerms } from '@/lib/contour';
import { formatDate, formatMoney } from '@/lib/format';
import { fromActLine, fromInput, isoInDays, lineProblem, sumOf, toInput, type LineDraft } from '@/lib/lines';

/**
 * The act the studio assembles: client, project, period, number, date, a summary of what was done and lines
 * picked from milestones, Linear tasks, invoices not yet closed by an act, or typed by hand.
 * «Отправить на подпись» saves, draws the PDF and tells the client.
 */
export function ActEditorDialog({
  clientId,
  projects,
  act,
  projectId,
  open,
  onClose,
}: {
  clientId: string;
  projects: Project[];
  /** A draft being edited; absent for a new act. */
  act?: Act;
  /** Project the new act starts in (the act of a project's own page). */
  projectId?: string;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} title={act ? `Акт № ${act.number}: черновик` : 'Выставить акт'} wide>
      <ActForm clientId={clientId} projects={projects} act={act} projectId={projectId} onClose={onClose} />
    </Dialog>
  );
}

function ActForm({ clientId, projects, act, projectId: startProject, onClose }: { clientId: string; projects: Project[]; act?: Act; projectId?: string; onClose: () => void }) {
  const create = useCreateAct();
  const update = useUpdateAct();
  const send = useSendAct();
  const { currency } = useStudioTerms();
  const [projectId, setProjectId] = useState(act?.projectId ?? startProject ?? '');
  const [periodFrom, setPeriodFrom] = useState(act?.periodFrom ?? '');
  const [periodTo, setPeriodTo] = useState(act?.periodTo ?? '');
  const [number, setNumber] = useState(act?.number ?? '');
  const [date, setDate] = useState(act?.issuedAt ?? isoInDays(0));
  const [basis, setBasis] = useState(act?.basis ?? '');
  const [summary, setSummary] = useState(act?.summary ?? '');
  const [lines, setLines] = useState<LineDraft[]>(() => act?.lines?.map(fromActLine) ?? []);
  const [invoiceIds, setInvoiceIds] = useState<string[]>(act?.invoiceIds ?? []);
  const [shown, setShown] = useState(false);
  const sources = useActSources({ clientId, projectId: projectId || undefined, periodFrom: periodFrom || undefined, periodTo: periodTo || undefined, actId: act?.id });
  const src = sources.data;
  const total = sumOf(lines, currency);
  const pending = create.isPending || update.isPending || send.isPending;
  const error = (create.error ?? update.error ?? send.error) as Error | null;
  const badPeriod = !!periodFrom && !!periodTo && periodFrom > periodTo;
  const sendable = lines.length > 0 && lines.every((l) => !lineProblem(l, true)) && total.amountMinor > 0;

  const has = (source: string) => lines.some((l) => l.source === source);
  const toggleLines = (source: string, picked: ActSourceLine[]) => setLines((ls) => (ls.some((l) => l.source === source) ? ls.filter((l) => l.source !== source) : [...ls, ...picked.map(fromInput)]));
  const toggleInvoice = (id: string, picked: ActSourceLine[]) => {
    toggleLines(`invoice:${id}`, picked);
    setInvoiceIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  };

  const body = () => ({
    projectId: projectId || null,
    periodFrom: periodFrom || null,
    periodTo: periodTo || null,
    date: date || null,
    basis: basis.trim(),
    summary: summary.trim(),
    lines: lines.map(toInput),
    invoiceIds,
    ...(number.trim() ? { number: number.trim() } : {}),
  });
  /** Saves the draft; then `after` runs with the saved act (send it, or just close). */
  const save = (after: (a: Act) => void) => {
    setShown(true);
    if (badPeriod) return;
    if (act) update.mutate({ id: act.id, patch: body() }, { onSuccess: after });
    else create.mutate({ clientId, input: body() }, { onSuccess: after });
  };
  const submit = () => save(onClose);
  const sendNow = () => {
    setShown(true);
    if (!sendable) return;
    save((a) => send.mutate(a.id, { onSuccess: onClose }));
  };

  return (
    <form
      className="space-y-5 p-5"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <label className="block text-sm sm:col-span-2">
          <span className="mb-1.5 block text-muted">Проект</span>
          <select className="field" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Без проекта</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Период с</span>
          <input className="field" type="date" value={periodFrom} max={periodTo || undefined} onChange={(e) => setPeriodFrom(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">по</span>
          <input className="field" type="date" value={periodTo} min={periodFrom || undefined} onChange={(e) => setPeriodTo(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Номер</span>
          <input className="field" maxLength={20} placeholder="следующий по порядку" value={number} onChange={(e) => setNumber(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Дата акта</span>
          <input className="field" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="block text-sm sm:col-span-2">
          <span className="mb-1.5 block text-muted">Основание</span>
          <input className="field" maxLength={300} placeholder="Договор № …, счета, запрос" value={basis} onChange={(e) => setBasis(e.target.value)} />
        </label>
      </div>
      {badPeriod && (
        <p role="alert" className="text-sm text-overdue">
          Период: начало позже конца.
        </p>
      )}

      <fieldset className="space-y-3">
        <legend className="text-sm text-muted">Что включить в акт</legend>
        {!src ? (
          <p className="text-sm text-muted">{sources.isError ? 'Источники не загрузились: строки можно добавить вручную.' : 'Загружаем…'}</p>
        ) : (
          <>
            <SourceGroup title="Этапы проекта (завершённые)" empty="Завершённых этапов нет.">
              {src.milestones.map((m) => (
                <SourceRow key={m.id} checked={has(m.id)} onToggle={() => toggleLines(m.id, [m.line])} note={m.used ? 'уже в другом акте' : undefined} label={m.line.title} aria={`Этап «${m.line.title}»`} hint={m.projectTitle} />
              ))}
            </SourceGroup>
            <SourceGroup title="Задачи Linear, завершённые за период" empty={src.tasksUnavailable ? 'Linear сейчас недоступен: задачи можно добавить вручную.' : 'Завершённых задач за период нет.'}>
              {src.tasks.map((t) => (
                <SourceRow key={t.id} checked={has(t.id)} onToggle={() => toggleLines(t.id, [t.line])} note={t.used ? 'уже в другом акте' : undefined} label={`${t.key} · ${t.line.title.replace(` (${t.key})`, '')}`} aria={`Задача ${t.key}`} hint={`завершена ${formatDate(t.doneAt)}; цену впишите сами`} />
              ))}
            </SourceGroup>
            <SourceGroup title="Счета без акта" empty="Все счета уже закрыты актами.">
              {src.invoices.map((i) => (
                <SourceRow key={i.id} checked={invoiceIds.includes(i.id)} onToggle={() => toggleInvoice(i.id, i.lines)} label={`Счёт № ${i.number} от ${formatDate(i.issuedAt)}`} aria={`Счёт № ${i.number}`} hint={i.status === 'paid' ? 'оплачен' : 'выставлен'} right={formatMoney(i.total)} />
              ))}
            </SourceGroup>
          </>
        )}
      </fieldset>

      <label className="block text-sm">
        <span className="mb-1.5 block text-muted">Что сделано (печатается над таблицей)</span>
        <textarea className="field min-h-20" maxLength={5000} value={summary} onChange={(e) => setSummary(e.target.value)} />
      </label>

      <LinesEditor lines={lines} onChange={setLines} currency={currency} allowFreePrice showProblems={shown} label="Строки акта" />

      {shown && !sendable && lines.length > 0 && total.amountMinor <= 0 && (
        <p role="alert" className="text-sm text-overdue">
          Чтобы отправить акт, укажите цены: сумма акта должна быть больше нуля.
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-overdue">
          {error.message}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
        <span className="num font-semibold">
          <span className="mr-2 text-sm font-normal text-muted">Итого</span>
          {formatMoney(total)}
          <span className="ml-3 text-xs font-normal text-muted">{invoiceIds.length ? `закрывает счетов: ${invoiceIds.length}` : 'без счетов'}</span>
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {act && (
            <DownloadButton className="btn-ghost" load={() => studioApi.studioActPdf(act.id)} name={`Акт № ${act.number}.pdf`} label="Предпросмотр PDF">
              Предпросмотр PDF
            </DownloadButton>
          )}
          <button type="submit" className="btn-secondary" disabled={pending}>
            Сохранить черновик
          </button>
          <button type="button" className="btn-primary" disabled={pending} onClick={sendNow}>
            Отправить на подпись
          </button>
        </div>
      </div>
    </form>
  );
}

function SourceGroup({ title, empty, children }: { title: string; empty: string; children: React.ReactNode[] }) {
  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">{title}</h3>
      {children.length ? <ul className="divide-y divide-line rounded shadow-hair">{children}</ul> : <p className="text-sm text-muted">{empty}</p>}
    </div>
  );
}

function SourceRow({ checked, onToggle, label, aria, hint, note, right }: { checked: boolean; onToggle: () => void; label: string; aria: string; hint?: string; note?: string; right?: string }) {
  return (
    <li className="flex items-center gap-3 px-3 py-2 text-sm">
      <label className="flex min-w-0 flex-1 items-center gap-3">
        <input type="checkbox" checked={checked} onChange={onToggle} aria-label={aria} />
        <span className="min-w-0">
          <span className="block truncate font-medium">{label}</span>
          {(hint || note) && <span className="block text-xs text-muted">{[hint, note].filter(Boolean).join(' · ')}</span>}
        </span>
      </label>
      {right && <span className="num text-sm">{right}</span>}
    </li>
  );
}
